export const sqlOperatorByFilterType: Record<string, string> = {
  contains: "LIKE",
  "does not contain": "NOT LIKE",
  is: "=",
  "is not": "!=",
  "starts with": "LIKE",
  "ends with": "LIKE",
  "is not set": "IS NULL",
  "is set": "IS NOT NULL",
};

/**
 * Validates filter operator against whitelist of allowed operators
 * Prevents injection through filter type field
 */
export const isValidFilterOperator = (type: string): boolean => {
  return type in sqlOperatorByFilterType;
};

export const getApiOperator = (filterType: string): string => {
  // Default to LIKE if operator is invalid for safety
  return sqlOperatorByFilterType[filterType] || "LIKE";
};

/**
 * Validates column name format to prevent injection
 * Allows alphanumeric, underscores, and hyphens only
 */
export const isValidColumnName = (columnName: string): boolean => {
  if (!columnName || typeof columnName !== "string") return false;
  // Only allow letters, numbers, underscores, and hyphens
  return /^[a-zA-Z0-9_-]+$/.test(columnName);
};

/**
 * Sanitizes user input to prevent XSS attacks
 * Escapes HTML special characters
 */
export const sanitizeInput = (input: string | number | boolean | null | undefined): string => {
  if (input === null || input === undefined) return "";
  
  const str = String(input);
  
  const htmlEscapeMap: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  
  return str.replace(/[&<>"']/g, (char) => htmlEscapeMap[char]);
};

/**
 * Validates that a value doesn't exceed maximum length
 * Prevents DoS through extremely long filter values
 */
export const isValidFilterLength = (value: string, maxLength: number = 1000): boolean => {
  return value.length <= maxLength;
};

/**
 * Validates that an ID is a valid numeric or string ID
 * Prevents ID injection attacks
 */
export const isValidId = (id: string | number): boolean => {
  if (typeof id === "number") {
    return id > 0 && Number.isInteger(id);
  }
  if (typeof id === "string") {
    // Allow numeric string or UUID-like string
    return id.length > 0 && /^[a-zA-Z0-9_-]+$/.test(id);
  }
  return false;
};

/**
 * Sanitizes object data for API submission
 * Recursively sanitizes string values, validates structure
 */
export const sanitizeObjectData = (
  obj: any,
  maxDepth: number = 5,
  currentDepth: number = 0
): any => {
  if (currentDepth > maxDepth) {
    return {};
  }

  if (obj === null || obj === undefined) {
    return null;
  }

  if (typeof obj === "string") {
    return sanitizeInput(obj);
  }

  if (typeof obj === "number" || typeof obj === "boolean") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeObjectData(item, maxDepth, currentDepth + 1));
  }

  if (typeof obj === "object") {
    const sanitized: any = {};
    for (const key in obj) {
      // Only allow alphanumeric keys, underscores, and hyphens
      if (/^[a-zA-Z0-9_-]+$/.test(key)) {
        sanitized[key] = sanitizeObjectData(obj[key], maxDepth, currentDepth + 1);
      }
    }
    return sanitized;
  }

  return obj;
};

// Date range operators
export const sqlOperatorByDateRangeType = {
  after: ">",
  before: "<",
  between: "BETWEEN",
  isNull: "IS NULL",
  isNotNull: "IS NOT NULL",
};

export const buildDateRangeQueries = (
  column: string,
  startValue: string | null,
  endValue: string | null,
  isNull?: boolean
): Record<string, string> => {
  const queries: Record<string, string> = {};

  // Handle IS NULL check (no value needed)
  if (isNull) {
    queries[column] = "";
    queries[`${column}_compare`] = sqlOperatorByDateRangeType.isNull;
    return queries;
  }
  
  // Sanitize and validate dates
  const sanitizedStart = startValue ? sanitizeInput(startValue) : null;
  const sanitizedEnd = endValue ? sanitizeInput(endValue) : null;
  
  // If both dates provided, use BETWEEN
  if (sanitizedStart && sanitizedEnd) {
    queries[column] = `${sanitizedStart},${sanitizedEnd}`;
    queries[`${column}_compare`] = sqlOperatorByDateRangeType.between;
  }
  // If only start date, use > (after)
  else if (sanitizedStart) {
    queries[column] = sanitizedStart;
    queries[`${column}_compare`] = sqlOperatorByDateRangeType.after;
  }
  // If only end date, use < (before)
  else if (sanitizedEnd) {
    queries[column] = sanitizedEnd;
    queries[`${column}_compare`] = sqlOperatorByDateRangeType.before;
  }
  
  return queries;
};

